"use client";

import { useState, useRef } from "react";
import { content, socialLinks } from "../../data/siteData";
import { localePath } from "../../lib/seo";

const ContactSection = ({ locale }) => {
  const t = content[locale].contact;
  const formRef = useRef(null);
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ type: "sending", message: t.sending });

    try {
      const formData = {
        name: e.target.name.value,
        email: e.target.email.value,
        message: e.target.message.value,
      };

      const response = await fetch(
        "https://contact-form-worker.alexandre2003gui.workers.dev",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setFormStatus({ type: "success", message: t.success });
      e.target.reset();
      setTimeout(() => setFormStatus({ type: "", message: "" }), 5000);
    } catch (error) {
      setFormStatus({
        type: "error",
        message: t.error,
      });
      setTimeout(() => setFormStatus({ type: "", message: "" }), 5000);
    }
  };

  return (
    <section
      id="contact"
      className="w-full py-16 md:py-24 px-6 md:px-12 bg-white"
    >
      <div className="max-w-4xl mx-auto flex flex-col gap-10 md:gap-16">
        <div className="text-center">
          <h2 className="font-poppins font-extralight text-4xl md:text-5xl lg:text-[64px] text-[#333333] leading-none mb-4">
            {t.heading}
          </h2>
          <p className="font-poppins font-light text-base md:text-xl text-[#555555]">
            {t.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 md:gap-6"
          >
            <input
              name="name"
              type="text"
              placeholder={t.name}
              required
              className="px-4 md:px-6 py-3 md:py-4 bg-[#f5f5f5] border-none rounded-lg font-poppins font-light text-base md:text-lg text-[#333333] placeholder:text-[#6b6b6b] focus:outline-none focus:ring-2 focus:ring-[#333333]"
              aria-label={t.nameLabel}
            />
            <input
              name="email"
              type="email"
              placeholder={t.email}
              required
              className="px-4 md:px-6 py-3 md:py-4 bg-[#f5f5f5] border-none rounded-lg font-poppins font-light text-base md:text-lg text-[#333333] placeholder:text-[#6b6b6b] focus:outline-none focus:ring-2 focus:ring-[#333333]"
              aria-label={t.emailLabel}
            />
            <textarea
              name="message"
              placeholder={t.message}
              rows="6"
              required
              className="px-4 md:px-6 py-3 md:py-4 bg-[#f5f5f5] border-none rounded-lg font-poppins font-light text-base md:text-lg text-[#333333] placeholder:text-[#6b6b6b] focus:outline-none focus:ring-2 focus:ring-[#333333] resize-none"
              aria-label={t.messageLabel}
            ></textarea>
            <button
              type="submit"
              disabled={formStatus.type === "sending"}
              className="px-6 md:px-8 py-3 md:py-4 bg-[#333333] text-white font-poppins font-light text-base md:text-lg rounded-lg hover:bg-[#555555] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#333333] focus:ring-offset-2"
            >
              {formStatus.type === "sending" ? t.sending : t.send}
            </button>
            <p className="font-poppins font-light text-xs md:text-sm text-[#666666]">
              {t.privacyNote}{" "}
              <a
                href={localePath(locale, "/datenschutz")}
                className="underline underline-offset-2 hover:text-[#555555]"
              >
                {t.privacyLink}
              </a>
              .
            </p>
            {formStatus.message && (
              <p
                className={`text-center font-poppins font-light ${
                  formStatus.type === "success"
                    ? "text-green-600"
                    : formStatus.type === "error"
                      ? "text-red-600"
                      : "text-gray-600"
                }`}
                role="status"
                aria-live="polite"
              >
                {formStatus.message}
              </p>
            )}
          </form>

          <div className="flex flex-col justify-center gap-6 md:gap-8">
            <div>
              <h3 className="font-poppins font-light text-xl md:text-2xl text-[#333333] mb-4">
                {t.connect}
              </h3>
              <div className="flex gap-4 md:gap-6">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#333333] hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-[#333333] rounded"
                    aria-label={social.label}
                  >
                    <social.icon
                      className="text-3xl md:text-4xl"
                      aria-hidden="true"
                    />
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-[#e0e0e0] pt-6 md:pt-8">
              <p className="font-poppins font-light text-base md:text-lg text-[#555555] mb-2">
                <span className="text-[#333333]">{t.locationLabel}</span> {t.location}
              </p>
              <p className="font-poppins font-light text-base md:text-lg text-[#555555]">
                <span className="text-[#333333]">{t.statusLabel}</span> {t.status}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
